import React, { useState, useEffect } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";

import { toast } from "react-toastify";
import socket from "../../services/socket-io";
import clsx from "clsx";

import { Paper, makeStyles, Box } from "@material-ui/core";
import { useTitle } from "../../hooks/useTitle";
import ContactDrawer from "../ContactDrawer";
import MessageInput from "../MessageInput/";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import TicketActionButtons from "../TicketActionButtons";
import MessagesList from "../MessagesList";
import api from "../../services/api";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import toastError from "../../errors/toastError";
import TagSelect from "../TagSelect"; 
import { hashDiagnosticId, useDiagnosticRender } from "../../services/frontendDiagnostics";

const drawerWidth = 320;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  },

  ticketInfo: {
    flex: "1 1 auto",
    minWidth: 0,
  },
  
  ticketActionButtons: {
    flexShrink: 0,
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      flexBasis: "100%",
      marginBottom: "5px",
    },
  },

  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: "0",
    marginRight: -drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  mainWrapperShift: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },
  tagsContainer: {
    padding: theme.spacing(1),
    paddingBottom: 0,
    marginTop: theme.spacing(1),
  },
}));

const Ticket = () => {
  const { ticketId } = useParams();
  const history = useHistory();
  const classes = useStyles();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  useDiagnosticRender("Ticket", () => ({
    ticketIdHash: hashDiagnosticId(ticketId),
    counts: {
      loading: loading ? 1 : 0,
      drawerOpen: drawerOpen ? 1 : 0,
      hasContact: contact.id ? 1 : 0,
      statusOpen: ticket.status === "open" ? 1 : 0
    }
  }));

  const chatTitle = ticket.id 
    ? (ticket.title || contact.name || "Chat") + " (#" + ticket.id + ")"
    : "Cargando Chat...";
    
  useTitle(chatTitle);
  
  const searchParams = new URLSearchParams(location.search);
  const highlightedMessageId = searchParams.get("messageId") || searchParams.get("messageID"); 
  
  const handleTicketTitleUpdate = (newTitle) => {
      setTicket(prevTicket => ({
          ...prevTicket,
          title: newTitle,
      }));
  };
  
  const handleTicketUpdate = (updatedTicket) => {
      setTicket(updatedTicket);
  };

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchTicket = async () => {
        try {
          const { data } = await api.get("/tickets/" + ticketId);

          setContact(data.contact);
          setTicket(data);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchTicket();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [ticketId, history]);

  useEffect(() => {
    socket.emit("joinChatBox", ticketId);

    const handleTicket = (data) => {
      if (data.action === "update" && String(data.ticket.id) === String(ticketId)) {
        setTicket(data.ticket);
      }

      if (data.action === "delete" && String(data.ticketId) === String(ticketId)) {
        if (!data.fromReopen) {
          toast.success("Ticket eliminado correctamente");
        }
        history.push("/tickets");
      }
    };

    const handleContact = (data) => {
      if (data.action === "update") {
        setContact((prevState) => {
          if (prevState.id === data.contact?.id) {
            return { ...prevState, ...data.contact };
          }
          return prevState;
        });
      }
    };

    socket.on("ticket", handleTicket);
    socket.on("contact", handleContact);

    return () => {
      socket.off("ticket", handleTicket);
      socket.off("contact", handleContact);
    };
  }, [ticketId, history]);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };
  
  const handleTagChange = (newTags) => {
    setContact(prev => ({ ...prev, tags: newTags }));
  };

  return (
    <div className={classes.root} id="drawer-container">
      <Paper
        variant="outlined"
        elevation={0}
        className={clsx(classes.mainWrapper, {
          [classes.mainWrapperShift]: drawerOpen,
        })}
      >
        <TicketHeader loading={loading}>
          <div className={classes.ticketInfo}>
            <TicketInfo
              contact={contact}
              ticket={ticket}
              onAvatarClick={handleDrawerOpen}
              onTicketTitleUpdate={handleTicketTitleUpdate}
            />
          </div>
          <div className={classes.ticketActionButtons}>
            <TicketActionButtons ticket={ticket} onTicketUpdate={handleTicketUpdate} />
          </div>
        </TicketHeader>
        {contact.id && (
          <Box className={classes.tagsContainer}>
            <TagSelect contactId={contact.id} onChange={handleTagChange} />
          </Box>
        )}
        <ReplyMessageProvider>
          <MessagesList
            ticketId={ticketId}
            isGroup={ticket.isGroup}
            highlightedMessageId={highlightedMessageId} 
          ></MessagesList>
          <MessageInput ticketStatus={ticket.status} />
        </ReplyMessageProvider>
      </Paper>
      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        contact={contact}
        loading={loading}
      />
    </div>
  );
};

export default Ticket;
